import Bacon from 'baconjs';
import * as ramda from 'ramda';

/**
 * @typedef {object} reportParamsFactory
 * @property {object} selected The current selecte report parameters
 * @property {function} updateReportParams(params) A function to update arbitrary parameters under the `report` key.
 */

/**
 *
 * This factory represents the params we need for our on-demand reports, stored under the `report` key of the URL.
 *
 * report.gap:
 *   The gap parameter specifies how we bucket the on-demand reports that work over time. Current valid values are:
 *     - d for days
 *     - w for weeks (default)
 *     - m for months
 *     - q for quarters
 *     - y for years
 *
 * report.type:
 *   The type parameter specifies whether to bucket leads by their closed or created time. Like report.gap, this will
 *   only apply to the hero chart and will not affect filters.
 *   - createdTime (default)
 *   - closedTime
 *
 * report.pivotFacet:
 *   This parameter tells us which facet to graph and order by on the pivot charts. The ordering happens on the server
 *   side, so changing this will trigger a new request for each pivot chart.
 *   - total           - The total value of all leads
 *   - count (default) - The total # of leads
 *
 * This only affects the Explore tab of the list page, and won't affect the actual filters.
 *
 * @param {object} dispatcher Our redux-like dispatcher object with push and stream functions.
 * @return {reportParamsFactory} With the current selected filters and a method to update them.
 */
export default function (dispatcher) {
    const {push, stream} = dispatcher;
    const urlStream = stream('URL').map('.query.report');
    const defaultParams = {
        gap: 'w',
        type: 'createdTime',
        pivotFacet: 'count',
    };

    /**
     * Provided an object of user-provided params, we'll validate them and, if invalid, overwrite them with the
     * default.
     * @param {object} params Params that came from the user
     * @return {object} Validated params object
     */
    const getValidParams = (params) => {
        const validGaps = ['d', 'w', 'm', 'q', 'y'];
        const validPivotFacets = ['total', 'count'];
        if (params.type !== 'createdTime' && params.type !== 'closedTime') {
            params.type = defaultParams.type;
        }
        if (!ramda.contains(params.gap, validGaps)) {
            params.gap = defaultParams.gap;
        }

        if (!ramda.contains(params.pivotFacet, validPivotFacets)) {
            params.pivotFacet = defaultParams.pivotFacet;
        }

        return params;
    };

    const selected = Bacon.update(
        {gap: 'w'},
        [urlStream],
        (currentParams, urlParams) => getValidParams(urlParams || defaultParams),
        [stream('PARAMS-REPORT-SET')],
        (currentParams, newParams) => getValidParams(Object.assign(currentParams, newParams))
    );

    return Bacon.combineTemplate({
        selected,
        updateReportParams(params) {
            push('PARAMS-REPORT-SET', params);
        },
    });
}
